/**
 * Purpose: Centralizes Tuitoria auth/session persistence for login and registration flows.
 * Responsibility: Reads, writes, validates, and clears legacy-compatible auth storage keys.
 * Affects: Login handoff, registration handoff, admin route guards, dashboard redirects.
 */

const AUTH_STORAGE_KEYS = [
    "admin_token",
    "pltfrm_admin_token",
    "pltfrmAdminToken",

    "user_token",
    "user_role",
    "admin_role",

    "user_email",
    "admin_email",
    "email",

    "platform_slug",
    "pltfrm_platform_slug",
    "admin_platform_slug",

    "session_token",
    "admin_session_token",
    "pltfrm_session_token",
    "sessionToken",

    "expires_at",

    "avatar_url",
    "admin_avatar_url",

    "admin_name",
    "user_name",

    "pltfrm_profile",
    "pltfrm_admin",
    "pltfrm_user",
    "pltfrm_auth",
    "pltfrm_admin_auth",

    "profile",
    "user",
    "me",
    "current_user",
    "currentUser",

    "user_id",
    "admin_id",

    "support_session_mode",
    "support_session_expires_at",
];

function safeTrim(value) {
    return String(value || "").trim();
}

function normalizeRole(value, isAdmin) {
    const role = safeTrim(value).toLowerCase();

    if (role) return role;
    return isAdmin ? "teacher" : "student";
}

function getLocalStorage() {
    try {
        if (typeof localStorage === "undefined") return null;
        return localStorage;
    } catch {
        return null;
    }
}

function getSessionStorage() {
    try {
        if (typeof sessionStorage === "undefined") return null;
        return sessionStorage;
    } catch {
        return null;
    }
}

export function readAuthStorageValue(key) {
    return readStorageValue(
        getAuthStorage(),
        key
    );
}

export function setAuthStorageBoth(key, value) {
    const normalized = String(value ?? "");

    try {
        getLocalStorage()?.setItem(key, normalized);
    } catch {}

    try {
        getSessionStorage()?.setItem(key, normalized);
    } catch {}
}

export function removeAuthStorageBoth(key) {
    try {
        getLocalStorage()?.removeItem(key);
    } catch {}

    try {
        getSessionStorage()?.removeItem(key);
    } catch {}
}

export function clearAuthSession() {
    AUTH_STORAGE_KEYS.forEach(removeAuthStorageBoth);

    try {
        Object.keys(getLocalStorage() || {}).forEach((key) => {
            if (
                key.startsWith("admin_name_") ||
                key.startsWith("admin_plan_") ||
                key.startsWith("admin_avatar_url:") ||
                key.startsWith("avatar_url:") ||
                key.startsWith("admin_avatar_url_") ||
                key.startsWith("avatar_url_")
            ) {
                getLocalStorage()?.removeItem(key);
            }
        });
    } catch {}

    try {
        Object.keys(getSessionStorage() || {}).forEach((key) => {
            if (
                key.startsWith("admin_name_") ||
                key.startsWith("admin_plan_") ||
                key.startsWith("admin_avatar_url:") ||
                key.startsWith("avatar_url:") ||
                key.startsWith("admin_avatar_url_") ||
                key.startsWith("avatar_url_")
            ) {
                getSessionStorage()?.removeItem(key);
            }
        });
    } catch {}
}

function getStorageSessionTokens(storage) {
    return [
        readStorageValue(storage, "session_token"),
        readStorageValue(storage, "admin_session_token"),
        readStorageValue(storage, "pltfrm_session_token"),
        readStorageValue(storage, "sessionToken"),
    ].filter(Boolean);
}

function getStoragePlatformSlug(storage) {
    return (
        readStorageValue(storage, "platform_slug") ||
        readStorageValue(storage, "pltfrm_platform_slug") ||
        readStorageValue(storage, "admin_platform_slug")
    );
}

function storageHasCompleteAuthSession(storage) {
    const email =
        readStorageValue(storage, "user_email") ||
        readStorageValue(storage, "admin_email") ||
        readStorageValue(storage, "email");

    return Boolean(
        email &&
        getStoragePlatformSlug(storage) &&
        getStorageSessionTokens(storage).length
    );
}

function storageMatchesAuthScope(
    storage,
    platformSlug,
    sessionToken
) {
    if (!storage) return false;

    const storedSessionTokens =
        getStorageSessionTokens(storage);

    if (sessionToken && storedSessionTokens.length) {
        return storedSessionTokens.includes(sessionToken);
    }

    if (!platformSlug) return false;

    return getStoragePlatformSlug(storage) === platformSlug;
}

export function clearAuthSessionScope({
    platformSlug = "",
    sessionToken = "",
} = {}) {
    const normalizedPlatformSlug =
        safeTrim(platformSlug);
    const normalizedSessionToken =
        safeTrim(sessionToken);

    const cleared = {
        local: false,
        session: false,
    };

    if (
        !normalizedPlatformSlug &&
        !normalizedSessionToken
    ) {
        return cleared;
    }

    const storages = [
        ["local", getLocalStorage()],
        ["session", getSessionStorage()],
    ];

    for (const [storageName, storage] of storages) {
        if (
            !storageMatchesAuthScope(
                storage,
                normalizedPlatformSlug,
                normalizedSessionToken
            )
        ) {
            continue;
        }

        for (const key of AUTH_STORAGE_KEYS) {
            try {
                storage.removeItem(key);
            } catch {}
        }

        if (normalizedPlatformSlug) {
            const scopedProfileKeys = [
                `admin_name_${normalizedPlatformSlug}`,
                `admin_plan_${normalizedPlatformSlug}`,
                `admin_avatar_url:${normalizedPlatformSlug}`,
                `avatar_url:${normalizedPlatformSlug}`,
                `admin_avatar_url_${normalizedPlatformSlug}`,
                `avatar_url_${normalizedPlatformSlug}`,
            ];

            for (const key of scopedProfileKeys) {
                try {
                    storage.removeItem(key);
                } catch {}
            }
        }

        cleared[storageName] = true;
    }

    return cleared;
}

export function persistAuthSession({
                                       email,
                                       role,
                                       isAdmin = false,
                                       platformSlug,
                                       sessionToken,
                                       expiresAt,
                                       name,
                                       avatarUrl,
                                       userId,
                                   } = {}) {
    const normalizedEmail = safeTrim(email);
    const normalizedSlug = safeTrim(platformSlug);
    const normalizedSessionToken = safeTrim(sessionToken);
    const normalizedRole = normalizeRole(role, isAdmin);
    const adminTokenValue = isAdmin ? "true" : "false";

    if (!normalizedEmail || !normalizedSlug || !normalizedSessionToken) {
        return false;
    }

    clearAuthSession();

    setAuthStorageBoth("admin_token", adminTokenValue);
    setAuthStorageBoth("pltfrm_admin_token", adminTokenValue);
    setAuthStorageBoth("pltfrmAdminToken", adminTokenValue);

    setAuthStorageBoth("user_token", "true");
    setAuthStorageBoth("user_role", normalizedRole);
    setAuthStorageBoth("admin_role", normalizedRole);

    setAuthStorageBoth("user_email", normalizedEmail);
    setAuthStorageBoth("admin_email", normalizedEmail);
    setAuthStorageBoth("email", normalizedEmail);

    setAuthStorageBoth("platform_slug", normalizedSlug);
    setAuthStorageBoth("pltfrm_platform_slug", normalizedSlug);
    setAuthStorageBoth("admin_platform_slug", normalizedSlug);

    setAuthStorageBoth("session_token", normalizedSessionToken);
    setAuthStorageBoth("admin_session_token", normalizedSessionToken);
    setAuthStorageBoth("pltfrm_session_token", normalizedSessionToken);

    if (expiresAt) {
        setAuthStorageBoth("expires_at", String(expiresAt));
    }

    if (userId !== null && userId !== undefined && safeTrim(userId) !== "") {
        setAuthStorageBoth("user_id", String(userId));
        if (isAdmin) setAuthStorageBoth("admin_id", String(userId));
    }

    if (name) {
        setAuthStorageBoth("user_name", name);
        if (isAdmin) {
            setAuthStorageBoth("admin_name", name);
            setAuthStorageBoth(`admin_name_${normalizedSlug}`, name);
        }
    }

    if (avatarUrl) {
        setAuthStorageBoth("avatar_url", avatarUrl);

        if (isAdmin) {
            setAuthStorageBoth("admin_avatar_url", avatarUrl);
            setAuthStorageBoth(`admin_avatar_url:${normalizedSlug}`, avatarUrl);
            setAuthStorageBoth(`avatar_url:${normalizedSlug}`, avatarUrl);
        }
    }

    const userPayload = {
        id: userId || null,
        email: normalizedEmail,
        name: name || "",
        avatar_url: avatarUrl || "",
        role: normalizedRole,
        is_admin: Boolean(isAdmin),
        platform_slug: normalizedSlug,
        expires_at: expiresAt || null,
        session_token: normalizedSessionToken,
    };

    setAuthStorageBoth("pltfrm_user", JSON.stringify(userPayload));
    setAuthStorageBoth("pltfrm_profile", JSON.stringify({ user: userPayload }));

    if (isAdmin) {
        setAuthStorageBoth("pltfrm_admin", JSON.stringify(userPayload));
    }

    return true;
}

function clearSessionAuthStorage(storage) {
    if (!storage) return;

    for (const key of AUTH_STORAGE_KEYS) {
        try {
            storage.removeItem(key);
        } catch {}
    }
}

export function persistSupportAdminSession({
    email,
    platformSlug,
    sessionToken,
    expiresAt,
    name,
    userId,
} = {}) {
    const normalizedEmail = safeTrim(email);
    const normalizedSlug = safeTrim(platformSlug);
    const normalizedToken = safeTrim(sessionToken);

    if (
        !normalizedEmail ||
        !normalizedSlug ||
        !normalizedToken.startsWith("sup_")
    ) {
        return false;
    }

    const storage = getSessionStorage();
    if (!storage) return false;

    clearSessionAuthStorage(storage);

    const setValue = (key, value) => {
        try {
            storage.setItem(
                key,
                String(value ?? "")
            );
        } catch {}
    };

    setValue("admin_token", "true");
    setValue("pltfrm_admin_token", "true");
    setValue("pltfrmAdminToken", "true");

    setValue("user_token", "true");
    setValue("user_role", "teacher");
    setValue("admin_role", "teacher");

    setValue("user_email", normalizedEmail);
    setValue("admin_email", normalizedEmail);
    setValue("email", normalizedEmail);

    setValue("platform_slug", normalizedSlug);
    setValue("pltfrm_platform_slug", normalizedSlug);
    setValue("admin_platform_slug", normalizedSlug);

    setValue("session_token", normalizedToken);
    setValue("admin_session_token", normalizedToken);
    setValue("pltfrm_session_token", normalizedToken);
    setValue("sessionToken", normalizedToken);

    if (expiresAt) {
        setValue("expires_at", expiresAt);
        setValue(
            "support_session_expires_at",
            expiresAt
        );
    }

    if (userId !== null && userId !== undefined) {
        setValue("user_id", userId);
        setValue("admin_id", userId);
    }

    if (name) {
        setValue("user_name", name);
        setValue("admin_name", name);
    }

    setValue("support_session_mode", "view");

    const userPayload = {
        id: userId || null,
        email: normalizedEmail,
        name: name || "",
        avatar_url: "",
        role: "teacher",
        is_admin: true,
        platform_slug: normalizedSlug,
        expires_at: expiresAt || null,
        session_token: normalizedToken,
    };

    setValue(
        "pltfrm_user",
        JSON.stringify(userPayload)
    );
    setValue(
        "pltfrm_profile",
        JSON.stringify({
            user: userPayload,
        })
    );
    setValue(
        "pltfrm_admin",
        JSON.stringify(userPayload)
    );

    return true;
}

export function persistSupportStudentSession({
    email,
    platformSlug,
    sessionToken,
    expiresAt,
    name,
    userId,
} = {}) {
    const normalizedEmail = safeTrim(email);
    const normalizedSlug = safeTrim(platformSlug);
    const normalizedToken = safeTrim(sessionToken);

    if (
        !normalizedEmail ||
        !normalizedSlug ||
        !normalizedToken.startsWith("stu_sup_")
    ) {
        return false;
    }

    const storage = getSessionStorage();
    if (!storage) return false;

    clearSessionAuthStorage(storage);

    const setValue = (key, value) => {
        try {
            storage.setItem(
                key,
                String(value ?? "")
            );
        } catch {}
    };

    setValue("admin_token", "false");
    setValue("pltfrm_admin_token", "false");
    setValue("pltfrmAdminToken", "false");

    setValue("user_token", "true");
    setValue("user_role", "student");
    setValue("admin_role", "student");

    setValue("user_email", normalizedEmail);
    setValue("email", normalizedEmail);

    setValue("platform_slug", normalizedSlug);
    setValue(
        "pltfrm_platform_slug",
        normalizedSlug
    );

    setValue("session_token", normalizedToken);
    setValue(
        "pltfrm_session_token",
        normalizedToken
    );
    setValue("sessionToken", normalizedToken);

    if (expiresAt) {
        setValue("expires_at", expiresAt);
        setValue(
            "support_session_expires_at",
            expiresAt
        );
    }

    if (
        userId !== null &&
        userId !== undefined
    ) {
        setValue("user_id", userId);
    }

    if (name) {
        setValue("user_name", name);
    }

    setValue(
        "support_session_mode",
        "student_view"
    );

    const userPayload = {
        id: userId || null,
        email: normalizedEmail,
        name: name || "",
        avatar_url: "",
        role: "student",
        is_admin: false,
        platform_slug: normalizedSlug,
        expires_at: expiresAt || null,
        session_token: normalizedToken,
    };

    setValue(
        "pltfrm_user",
        JSON.stringify(userPayload)
    );
    setValue(
        "pltfrm_profile",
        JSON.stringify({
            user: userPayload,
        })
    );

    return true;
}


export function hasSupportStudentSession() {
    const storage = getSessionStorage();

    if (!storage) return false;

    const mode = readStorageValue(
        storage,
        "support_session_mode"
    );

    const token =
        getStorageSessionTokens(storage)[0] || "";

    return (
        mode === "student_view" &&
        token.startsWith("stu_sup_")
    );
}


export function clearSupportStudentSession() {
    if (!hasSupportStudentSession()) {
        return false;
    }

    clearSessionAuthStorage(
        getSessionStorage()
    );

    return true;
}


export function consumeSupportStudentHandoff() {
    if (
        typeof window === "undefined" ||
        !window.location.hash
    ) {
        return false;
    }

    const params = new URLSearchParams(
        window.location.hash.slice(1)
    );

    const sessionToken = safeTrim(
        params.get("support_session")
    );

    if (
        !sessionToken.startsWith(
            "stu_sup_"
        )
    ) {
        return false;
    }

    const pathParts =
        window.location.pathname
            .split("/")
            .filter(Boolean);

    if (
        pathParts.length < 2 ||
        pathParts[1] !== "student"
    ) {
        return false;
    }

    const platformSlug = safeTrim(
        pathParts[0]
    );

    const saved =
        persistSupportStudentSession({
            email: params.get(
                "support_email"
            ),
            platformSlug,
            sessionToken,
            expiresAt: params.get(
                "support_expires_at"
            ),
            name: params.get(
                "support_name"
            ),
            userId: params.get(
                "support_user_id"
            ),
        });

    if (!saved) {
        return false;
    }

    window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}` +
            `${window.location.search}`
    );

    return true;
}


export function hasSupportAdminSession() {
    const storage = getSessionStorage();

    if (!storage) return false;

    const mode = readStorageValue(
        storage,
        "support_session_mode"
    );

    const token =
        getStorageSessionTokens(storage)[0] || "";

    return (
        mode === "view" &&
        token.startsWith("sup_")
    );
}

export function clearSupportAdminSession() {
    if (!hasSupportAdminSession()) {
        return false;
    }

    clearSessionAuthStorage(
        getSessionStorage()
    );

    return true;
}

export function consumeSupportAdminHandoff() {
    if (
        typeof window === "undefined" ||
        !window.location.hash
    ) {
        return false;
    }

    const params = new URLSearchParams(
        window.location.hash.slice(1)
    );

    const sessionToken = safeTrim(
        params.get("support_session")
    );

    if (!sessionToken.startsWith("sup_")) {
        return false;
    }

    const pathParts = window.location.pathname
        .split("/")
        .filter(Boolean);

    if (
        pathParts.length < 2 ||
        pathParts[1] !== "admin"
    ) {
        return false;
    }

    const platformSlug = safeTrim(
        pathParts[0]
    );

    const saved = persistSupportAdminSession({
        email: params.get("support_email"),
        platformSlug,
        sessionToken,
        expiresAt: params.get(
            "support_expires_at"
        ),
        name: params.get("support_name"),
        userId: params.get(
            "support_user_id"
        ),
    });

    if (!saved) {
        return false;
    }

    window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}` +
            `${window.location.search}`
    );

    return true;
}


function readStorageValue(storage, key) {
    try {
        return safeTrim(storage?.getItem(key));
    } catch {
        return "";
    }
}

function getAuthStorage() {
    const local = getLocalStorage();
    const session = getSessionStorage();

    // sessionStorage belongs to the active browser window/tab.
    // localStorage is shared across windows and is only a fallback.
    if (storageHasCompleteAuthSession(session)) {
        return session;
    }

    if (storageHasCompleteAuthSession(local)) {
        return local;
    }

    // Preserve compatibility with older partial auth storage.
    if (getStorageSessionTokens(session).length) {
        return session;
    }

    if (getStorageSessionTokens(local).length) {
        return local;
    }

    return session || local;
}

export function getAuthSession() {
    const storage = getAuthStorage();

    const adminToken =
        readStorageValue(storage, "admin_token") === "true" ||
        readStorageValue(storage, "pltfrm_admin_token") === "true" ||
        readStorageValue(storage, "pltfrmAdminToken") === "true";

    const sessionToken =
        readStorageValue(storage, "session_token") ||
        readStorageValue(storage, "admin_session_token") ||
        readStorageValue(storage, "pltfrm_session_token") ||
        readStorageValue(storage, "sessionToken");

    const platformSlug =
        readStorageValue(storage, "platform_slug") ||
        readStorageValue(storage, "pltfrm_platform_slug") ||
        readStorageValue(storage, "admin_platform_slug");

    const email =
        readStorageValue(storage, "user_email") ||
        readStorageValue(storage, "admin_email") ||
        readStorageValue(storage, "email");

    const role =
        readStorageValue(storage, "user_role") ||
        readStorageValue(storage, "admin_role");

    const expiresAt =
        readStorageValue(storage, "expires_at");

    return {
        isAuthenticated: Boolean(email && sessionToken),
        isAdmin: Boolean(adminToken),
        email: safeTrim(email),
        role: safeTrim(role).toLowerCase(),
        platformSlug: safeTrim(platformSlug),
        sessionToken: safeTrim(sessionToken),
        expiresAt: safeTrim(expiresAt),
    };
}

export function hasAdminSession() {
    const session = getAuthSession();

    return Boolean(
        session.isAuthenticated &&
        session.isAdmin &&
        session.platformSlug &&
        session.sessionToken
    );
}

export function getDashboardUrl(fallback = "/admin") {
    const session = getAuthSession();

    if (session.isAdmin && session.platformSlug) {
        return `/${session.platformSlug}/admin`;
    }

    return fallback;
}